import {Component, computed, DestroyRef, effect, input, model, output, signal, untracked} from '@angular/core';

import * as _Highcharts from 'highcharts';
import 'highcharts/esm/highcharts-more';
import 'highcharts/esm/modules/solid-gauge';
import 'highcharts/esm/modules/boost';
import 'highcharts/esm/modules/exporting';
import 'highcharts/esm/modules/export-data';
import 'highcharts/esm/modules/broken-axis';
import 'highcharts/esm/modules/variable-pie';
import {UserDefinedWidgetType} from '@constants/pages/user-defined-page/user-defined-widget-type';
import {TranslationService} from '@services/translation/translation.service';
import {UserDefinedChartDetails} from '@models/pages/user-defined-page/user-defined-chart-details';
import {Machine} from '@models/machine/machine';
import {ChartSignalEntry} from '@models/pages/user-defined-page/chart-signal-entry';
import {GeneralChartSettingsGeneratorService} from '@services/charts/general-chart-settings-generator.service';
import {LineChartSettingsParameters} from '@models/charts/line-chart-settings-parameters';
import {GeneralChartDataUpdateService} from '@services/charts/general-chart-data-update.service';
import {Signal} from '@models/dashboard/signal';
import {NotificationService} from '@services/notification/notification.service';
import {ChartConstructorType} from 'highcharts-angular';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';


@Component({
    selector: 'scitis-highcharts-chart',
    templateUrl: './highcharts-chart.component.html',
    styleUrls: ['./highcharts-chart.component.scss'],
    standalone: false
})
export class HighchartsChartComponent {

  widgetType = input<UserDefinedWidgetType>(UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS);
  machine = input<Machine>(new Machine());
  chartSettings = input<UserDefinedChartDetails | undefined>(undefined);
  data = input<any>({});
  chartId = input<string>('');
  moveToInteractiveDataAnalysis = input<string>('');
  isInEditMode = input<boolean>(false);
  forceHighChartsChartReflow = input<boolean>(false);
  isTemplateWidget = input<boolean>(false);

  moveToInteractiveDataAnalysisEvent = output<ChartSignalEntry[]>();

  highcharts: typeof _Highcharts = _Highcharts;
  chartConstructor: ChartConstructorType = 'chart';
  chartOptions = signal<_Highcharts.Options>({});
  updateFlag = model<boolean>(false);
  oneToOneFlag = signal<boolean>(false);
  showChart = signal<boolean>(false);

  isChartBar = computed(() => this.widgetType() === UserDefinedWidgetType.BAR);
  isChartGauge = computed(() => this.widgetType() === UserDefinedWidgetType.GAUGE);
  isChartLine = computed(() => this.widgetType() === UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS);

  private chartInstance: _Highcharts.Chart | undefined;
  private pendingData: any[] | undefined;
  private previousData: any[] = [];

  constructor(private generalChartSettingsGeneratorService: GeneralChartSettingsGeneratorService,
              private generalChartDataUpdateService: GeneralChartDataUpdateService,
              private translationService: TranslationService,
              private notificationService: NotificationService,
              destroyRef: DestroyRef) {

    this.translationService.translate.onLangChange
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => {
        if (this.chartSettings()) {
          this.generateChartOptions();
        }
      });

    effect(() => {
      const settings = this.chartSettings();
      this.isInEditMode();
      if (!settings) {
        return;
      }
      untracked(() => this.generateChartOptions());
    });

    effect(() => {
      const currentData = this.data();
      untracked(() => {
        if (currentData && currentData.length > 0) {
          let dataToUse: any[];
          if (this.isChartLine() && this.previousData.length > 1) {
            dataToUse = this.previousData.concat(currentData);
          } else {
            dataToUse = currentData;
          }
          this.previousData = currentData;
          this.updateChartData(dataToUse);
        }
      });
    });

    effect(() => {
      const idaValue = this.moveToInteractiveDataAnalysis();
      untracked(() => {
        if (idaValue && !idaValue.isNullOrWhitespace()) {
          this.getVisibleSignalsAndConfiguration();
        }
      });
    });

    effect(() => {
      if (this.forceHighChartsChartReflow()) {
        untracked(() => {
          const chart = this.chartInstance
              ?? this.generalChartDataUpdateService.getHighChartById(this.chartId(), this.highcharts.charts);
          if (chart) {
            chart.redraw();
          }
        });
      }
    });
  }

  public onChartInstance(chart: _Highcharts.Chart): void {
    this.chartInstance = chart;
    if (this.pendingData) {
      const data = this.pendingData;
      this.pendingData = undefined;
      this.updateChartData(data);
    }
  }

  private getVisibleSignalsAndConfiguration() {
    const chart: _Highcharts.Chart | undefined = this.chartInstance
        ?? this.generalChartDataUpdateService.getHighChartById(this.chartId(), this.highcharts.charts);
    const settings = this.chartSettings();
    if (!chart || !settings) {
      return;
    }

    const seriesList: any[] = chart.series;
    const visibleSignalsIds: number[] = [];

    for (const series of seriesList) {
      //do not add axis signals
      if (series.linkedSeries && series.linkedSeries.length > 0) {
        continue;
      }

      if (series.visible) {
        const id = series.options.id;
        visibleSignalsIds.push(Number(id));
      }
    }
    const visibleSignalsDetails: ChartSignalEntry[] = [];
    let filteredOutSignals: ChartSignalEntry[] = [];
    for (const axis of settings.axis) {
      const axisSignals = this.filterOutCalcSignalsThatDontStreamToBQ(axis.signals, this.machine().signals);
      filteredOutSignals = [...filteredOutSignals, ...axis.signals.filter(signal => !(new Set(axisSignals.map(item => item.signalName)).has(signal.signalName)))] ;
      for (const signal of axisSignals) {
        if (visibleSignalsIds.includes(signal.signalId)) {
          const newSignal: ChartSignalEntry = {...signal};
          newSignal.axis = axis.position;
          newSignal.minY = axis.minY;
          newSignal.maxY = axis.maxY;
          newSignal.customRange = true;
          newSignal.label = this.getSignalLabel(signal);
          newSignal.displayedText = newSignal.label;
          newSignal.axisYLabel = newSignal.label;

          visibleSignalsDetails.push(newSignal);
        }
      }

    }
    if (visibleSignalsDetails.length > 0) {
      if (filteredOutSignals.length > 0) {
        let warningMessage = '';
        filteredOutSignals.forEach((signal)=> {
          warningMessage += `"${this.translationService.translate.instant(signal.label)}", `;
        });
        this.notificationService.warning(`Calculated signal(s) ${warningMessage.slice(0,-2)} is/are not available`,'User defined page', 'Forward to IDA',false);
      }
      this.moveToInteractiveDataAnalysisEvent.emit(visibleSignalsDetails);
    }


  }

  private getSignalLabel(signal: ChartSignalEntry): string {
    if (!signal?.signalId) {
      return this.translationService.translate.instant(signal.label);
    }
    const id = Number(signal.signalId);
    if (!this.machine().signals) {
      return this.translationService.translate.instant(signal.label);
    }
    const machineSignals = Object.values(this.machine().signals) as Signal[];

    const machineSignal = machineSignals.find((item: Signal) => {
      return item.id === id;
    });
    if (!machineSignal) {
      return this.translationService.translate.instant(signal.label);
    }
    return this.translationService.translate.instant(machineSignal.label);

  }

  private generateChartOptions() {
    const chartParameters = this.generateChartParameters();
    this.chartOptions.set(this.generalChartSettingsGeneratorService.generateChartSettings(this.widgetType(), chartParameters));
    this.generalChartSettingsGeneratorService.adjustChartInteraction(chartParameters);
    this.updateFlag.set(true);
    this.showChart.set(true);
  }

  private updateChartData(data: any) {
    const chartParameters = this.generateChartParameters();
    const chart = this.chartInstance
        ?? this.generalChartDataUpdateService.getHighChartById(this.chartId(), this.highcharts.charts);
    if (!chart) {
      this.pendingData = data;
      return;
    }
    this.generalChartDataUpdateService.updateChartData(this.widgetType(), chart, chartParameters, data);
  }

  private generateChartParameters(): LineChartSettingsParameters {
    const chartParameters: LineChartSettingsParameters = new LineChartSettingsParameters();
    chartParameters.isLineChart = true;
    chartParameters.isTemplateWidget = this.isTemplateWidget();
    chartParameters.isInEditMode = this.isInEditMode();
    chartParameters.machineId = String(this.machine().id);
    chartParameters.highchartsChart = this.chartInstance
        ?? this.generalChartDataUpdateService.getHighChartById(this.chartId(), this.highcharts.charts);
    chartParameters.machineSignals = this.machine().signals;
    chartParameters.chartSettings = this.chartSettings();
    chartParameters.data = this.data();
    chartParameters.chartId = this.chartId();
    chartParameters.portalLanguage = this.translationService.getActiveLanguage();
    return chartParameters;
  }

  private filterOutCalcSignalsThatDontStreamToBQ(axisSignals: ChartSignalEntry[], machineSignals: any) {
    const signalMap = new Map<string, { calculated: boolean, streamToBigQuery: boolean }>();
    console.log(Object.values(machineSignals));
    Object.values(machineSignals).forEach((item: any) => signalMap.set(item.name, { calculated: item.calculated, streamToBigQuery: item.streamToBigQuery }));

    return axisSignals.filter(item => {
      const filterConditions = signalMap.get(item.signalName);
      return filterConditions && ((filterConditions.calculated && filterConditions.streamToBigQuery) || !filterConditions.calculated);
    });
  }
}
